import { useEffect, useState } from 'react';
import { isItUndefined, pushDataLayerEvent } from '../../../util/DataUtil';
import { Breadcrumb } from '../breadcrumb';
import { useRouter } from 'next/router';
import {
  getPageFromQuery,
  getSearchFromQuery,
  stripListingParams,
} from '../../../util/PaginationUtil';
import { getCookie } from 'cookies-next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { Constants } from '../../../util/Constants';
import { useDispatch, useSelector } from 'react-redux';
import { setScreenSize } from '../../../store/pageSlice';
import Header from '../header/header';
import { ixnRedirectMessages } from './ixnRedirectMessages';
import AuthorSchema from './authorSchema';
import {
  getScriptForLanguage,
  getDomainAlias,
  getCanonical,
  getPaginationRelPrev,
  getSearchRobotsMeta,
  getLanguageContent,
  renderAlternateLinks,
  onScroll,
  getLandPageFromRedirect,
  removeQueryParams,
  getDevice,
  setOperatingSystemClass,
} from '../../../util/LayoutUtil';
import { extractMetaTags, renderMetaTags } from './layoutMetaUtils';
const Footer = dynamic(() => import('../footer'));
//layout
const Layout = ({
  urlAlias,
  data,
  meta,
  lang,
  children,
  menuData,
  footerData,
  globalMetrics,
  countryData,
  endpoint,
  contentType,
  languageSelectorData,
}) => {
  const hideElements = useSelector((state) => state.pageSlice);
  const isBrowser = typeof window == 'undefined';
  const [viewportWidth, setViewportWidth] = useState(
    !isBrowser ? window?.innerWidth : 0
  );
  const dispatch = useDispatch();
  const heroBlock = data?.field_template_blocks?.find((x) => x?.hero_block);
  const host = useSelector((state) => state.host.host);
  const [isIxnquery, setisIxnQuery] = useState(false);
  const [device, setDevice] = useState();
  const { metaTags, canonicalUrl, titleTag, description } =
    extractMetaTags(data);
  const existingPages = data.translang;
  useEffect(() => {
    setDevice(getDevice(viewportWidth));
  }, [viewportWidth]);
  const [headScript, setHeadScript] = useState('');
  const [noScript, setNoScript] = useState('');
  const [oneTrustScript, setOneTrustScript] = useState('');
  const router = useRouter();

  // Page of a paginated listing, read straight from the URL. Drives the
  // self-referencing canonical below — a paginated URL must not canonicalise
  // back to page one, or search engines drop the rest of the series.
  // The page the canonical should claim.
  //
  // A plain `?page=2` canonicalises to itself — that is the whole point, and
  // without it search engines treat pages 2…n as duplicates of page one and
  // drop them.
  //
  // But a *filtered* or searched `?page=2` is different content from the
  // unfiltered `?page=2`, so pointing one at the other would assert they are
  // the same page when they are not. Those views canonicalise to the bare
  // listing instead, which is consistent with keeping filters out of the index.
  const listingFilters = stripListingParams(router?.query) || {};
  const hasNarrowing =
    Object.keys(listingFilters).length > 0 ||
    Boolean(getSearchFromQuery(router?.query));

  const paginationPage = hasNarrowing ? 0 : getPageFromQuery(router?.query);

  const getPageviewData = () => {
    const pathParts = (urlAlias ?? '')
      .toLowerCase()
      .replace(/-/g, ' ')
      .split('/')
      .filter(Boolean);

    // Same fallback order/label rules as before (only field_topics also
    // falls back to .name) but walked once so page_topic and page_topic_id
    // always resolve from the same term instead of two separate chains.
    const pageTopicSources = [
      { term: data?.field_topics?.[0], allowName: true },
      { term: data?.field_template_category?.[0], allowName: false },
      { term: data?.field_template_secondary_categor?.[0], allowName: false },
      { term: data?.field_template_tertiary_category?.[0], allowName: false },
    ];

    const labelOf = ({ term, allowName }) =>
      term?.field_source_translation || (allowName ? term?.name : undefined);

    const pageTopicSource = pageTopicSources.find(labelOf);

    const pageTopic = labelOf(pageTopicSource || {})?.toLowerCase();

    const pageTopicId = pageTopicSource?.term?.tid
      ? String(pageTopicSource.term.tid)
      : undefined;

    // English source name, so the value is the same on every language
    // ("selection", not "auswahl"/"sélection").
    const contentIntentTerm = data?.field_content_intent?.[0];
    const contentIntent = (
      contentIntentTerm?.field_source_translation || contentIntentTerm?.name
    )?.toLowerCase();

    const contentId = data?.node_id ? String(data.node_id) : undefined;

    const slugify = (name) =>
      name
        ?.toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-+|-+$)/g, '');

    const seenTagIds = new Set();
    const contentTags = [
      data?.field_template_category,
      data?.field_template_secondary_categor,
      data?.field_template_tertiary_category,
    ]
      .flat()
      .filter(Boolean)
      .reduce((tags, term) => {
        if (!term?.tid || seenTagIds.has(term.tid)) {
          return tags;
        }
        const name = term.field_source_translation || term.name;
        if (!name) {
          return tags;
        }
        seenTagIds.add(term.tid);
        tags.push({
          id: `tag-${term.tid}`,
          slug: slugify(name),
          name,
        });
        return tags;
      }, []);

    const contentFields = {
      ...(contentId && { content_id: contentId }),
      ...(pageTopicId && { page_topic_id: pageTopicId }),
      ...(contentTags.length > 0 && { content_tags: contentTags }),
    };

    if (pathParts[0] === 'resources' && pathParts[1]) {
      return {
        event: 'pageview',
        page_category: pathParts[0],
        page_type: pathParts[1],
        ...(pageTopic && { page_topic: pageTopic }),
        ...(contentIntent && { content_intent: contentIntent }),
        ...contentFields,
      };
    }

    if (
      contentType === 'region' ||
      contentType === 'metro' ||
      contentType === 'location'
    ) {
      return {
        event: 'pageview',
        page_category: pathParts[0],
        ...(pathParts[1] && { datacenter_region: pathParts[1] }),
        ...(pathParts[2] && { datacenter_metro: pathParts[2] }),
        ...(pathParts[3] && { datacenter_facility: pathParts[3] }),
        ...(pageTopic && { page_topic: pageTopic }),
        ...(contentIntent && { content_intent: contentIntent }),
        ...contentFields,
      };
    }

    return {
      event: 'pageview',
      page_category: pathParts[0] || '',
      ...(pageTopic && { page_topic: pageTopic }),
      ...(contentIntent && { content_intent: contentIntent }),
      ...contentFields,
    };
  };

  const pageviewData = getPageviewData();

  const dataLayerScript = `
    window.dataLayer = window.dataLayer || [];
    if (!window.dataLayerPushed) {
      window.dataLayer.push(${JSON.stringify(pageviewData)});
      window.dataLayerPushed = true;
    }
  `;

  useEffect(() => {
    const handleRouteChange = () => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(pageviewData);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [router.events, pageviewData]);

  useEffect(() => {
    const supportedExtensions = [
      'pdf',
      'doc',
      'docx',
      'xls',
      'xlsx',
      'ppt',
      'pptx',
      'zip',
    ];

    const isSupportedFile = (url) => {
      const ext = url.split('.').pop()?.split('?')[0];
      return supportedExtensions.includes(ext || '');
    };

    const pushDownloadEvent = (url) => {
      const normalizedUrl = url.toLowerCase();
      const fileName = normalizedUrl.split('/').pop()?.split('?')[0];

      pushDataLayerEvent('file_download', {
        event: 'file_download',
        file_name: fileName,
        file_url: normalizedUrl,
      });
    };

    // Track <a> clicks
    const handleDownloadClick = (e) => {
      const link = e.target.closest('a');
      if (!link?.href) return;

      const url = link.href.toLowerCase();
      if (isSupportedFile(url)) {
        pushDownloadEvent(url);
      }
    };

    document.addEventListener('click', handleDownloadClick);

    // Wrap window.open
    const originalWindowOpen = window.open;

    window.open = function (url, target, features) {
      if (url && isSupportedFile(url.toLowerCase())) {
        pushDownloadEvent(url);
      }
      return originalWindowOpen.call(window, url, target, features);
    };

    return () => {
      document.removeEventListener('click', handleDownloadClick);
      window.open = originalWindowOpen;
    };
  }, []);

  useEffect(() => {
    const languageToFontId = {
      ja: 'japanese-font',
      'zh-hans': 'chinese-font',
      ko: 'korean-font',
    };

    document.body.id = languageToFontId[lang] || '';
  }, [lang]);

  useEffect(() => {
    if (isIxnquery) {
      removeQueryParams();
      const timeoutId = setTimeout(() => {
        setisIxnQuery(false);
      }, 8000);

      return () => clearTimeout(timeoutId);
    }
  }, [isIxnquery]);

  const [userCountryData, setUserCountryData] = useState({
    userCountry: '',
    isDE: false,
    isLoading: true,
  });

  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/country/');
        const country = await response.text();

        setUserCountryData({
          userCountry: country,
          isDE: country === 'DE',
          isLoading: false,
        });

        window.userCountry = country;
        window.__isDE = country === 'DE';
      } catch (error) {
        console.error('Country detection failed:', error);
        setUserCountryData({
          userCountry: '',
          isDE: false,
          isLoading: false,
        });
        window.userCountry = '';
        window.__isDE = false;
      }
    };

    detectCountry();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    if (!isIxnquery) {
      const scrollbarThumb = document.querySelector('.overlay-scrollbar-thumb');
      const scrollbarTrack = document.querySelector('.overlay-scrollbar-track');

      scrollbarThumb.addEventListener('mousedown', (event) => {
        event.preventDefault();
        const startY = event.pageY;
        const startScrollTop = document.documentElement.scrollTop;

        document.addEventListener('mousemove', moveHandler);
        document.addEventListener('mouseup', cleanup);

        function moveHandler(event) {
          event.preventDefault();
          const delta = event.pageY - startY;
          document.documentElement.scrollTop = startScrollTop + delta / 1.15;
        }

        function cleanup() {
          document.removeEventListener('mousemove', moveHandler);
          document.removeEventListener('mouseup', cleanup);
        }
      });

      scrollbarTrack.addEventListener('mousedown', (event) => {
        event.preventDefault();
        const deltaY = event.offsetY - scrollbarThumb.offsetHeight / 2;
        document.documentElement.scrollTop += deltaY;
      });

      window.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    dispatch(setScreenSize(viewportWidth));
  }, [viewportWidth]);

  let origin = undefined;
  let sendHome = false;
  const [showBg, setShowBg] = useState(false);
  const [showSearchBg, setShowSearchBg] = useState(false);

  let adminCookie = getCookie('adminUser');

  // A draft/unpublished page is normally kicked to home unless an admin is
  // logged in. Preview requests (CMS "Preview" button) must be allowed through
  // so editors can see the draft — otherwise the client redirects to home even
  // though SSR served the draft correctly. asPath covers ?dlrpreview,
  // &dlrpreview and path-segment formats.
  const isPreview = (router?.asPath || '').includes('dlrpreview');

  if (
    data?.published !== '1' &&
    !isPreview &&
    (typeof adminCookie === 'undefined' || adminCookie === '{}')
  ) {
    sendHome = true;
  }

  useEffect(() => {
    if (sendHome) {
      sendHome = false;
      router.push('/');
    }
  }, [adminCookie]);

  if (typeof window !== 'undefined') {
    origin = window.location.origin;
  }

  useEffect(() => {
    const { query: queryParams } = router;
    if (queryParams.hasOwnProperty('inx')) {
      setisIxnQuery(true);
    }
  }, [router.query]);

  useEffect(() => {
    const handler = onScroll();
    window.addEventListener('scroll', handler, { passive: true });

    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, []);

  const toggleBg = () => {
    setShowBg(!showBg);
  };

  const offBg = () => {
    setShowBg(false);
  };

  const onBg = () => {
    setShowBg(true);
  };

  const toggleShowSearchBg = (searchActive) => {
    setShowSearchBg(searchActive);
  };

  useEffect(() => {
    setOperatingSystemClass();
  }, []);

  const GTM_CONFIG = {
    dlr: {
      gtmAuth: 'ErWcGdQjoYB34UBL4wE3QA',
      gtmPreview: 'env-961',
    },
    uat: {
      gtmAuth: '2z1s24laUs1wbuitRPhEHg',
      gtmPreview: 'env-975',
    },
    prod: {
      gtmAuth: 'MvZjD1ILbXFJIPD3ki8fnw',
      gtmPreview: 'env-42',
    },
  };

  const PROD_HOSTNAMES = [
    'digitalrealty.com',
    'www.digitalrealty.com',
    'digitalrealty.de',
    'www.digitalrealty.de',
    'digitalrealty.nl',
    'www.digitalrealty.nl',
    'digitalrealty.co.uk',
    'www.digitalrealty.co.uk',
    'digitalrealty.asia',
    'www.digitalrealty.asia',
    'digitalrealty.kr',
    'www.digitalrealty.kr',
    'digitalrealty.fr',
    'www.digitalrealty.fr',
    'digitalrealty.es',
    'www.digitalrealty.es',
    'digitalrealty.at',
    'www.digitalrealty.at',
    'digitalrealty.ie',
    'www.digitalrealty.ie',
    'digitalrealty.be',
    'www.digitalrealty.be',
    'digitalrealty.gr',
    'www.digitalrealty.gr',
    'digitalrealty.se',
    'www.digitalrealty.se',
    'digitalrealty.dk',
    'www.digitalrealty.dk',
    'digitalrealty.cn',
    'www.digitalrealty.cn',
    'digitalrealty.ch',
    'www.digitalrealty.ch',
  ];

  // Braidr server-side GTM loader for UAT (container GTM-5ZXBHKRJ via
  // ss-uat.digitalrealty.com). Replaces the standard googletagmanager.com
  // snippet on UAT only — prod and the dlr/local environments are untouched,
  // both because the ticket scopes this to uat.digitalrealty.com and because
  // this loader points at UAT-specific server-side infrastructure.
  const braidrUatLoader = `!function() {
  "use strict";
  function l(e) {
    for (var t = e, r = 0, n = document.cookie.split(";"); r < n.length; r++) {
      var o = n[r].split("=");
      if (o[0].trim() === t) return o[1]
    }
  }
  function s(e) { return localStorage.getItem(e) }
  function u(e) { return window[e] }
  function A(e, t) {
    e = document.querySelector(e);
    return t ? null == e ? void 0 : e.getAttribute(t) : null == e ? void 0 : e.textContent
  }
  var e = window, t = document, r = "script", n = "dataLayer",
    o = "https://ss-uat.digitalrealty.com",
    a = "https://load.ss-uat.digitalrealty.com",
    i = "d1p8zzzpqvvh",
    c = "d0cp4k=ARJLNiQ3V08yLjQ5OygwXB5LRF9BXB4UVRIfFhkOGxYaExcdBANUGQcb",
    g = "stapeUserId", v = "", E = "", d = !1;
  try {
    var d = !!g && (m = navigator.userAgent, !!(m = new RegExp("Version/([0-9._]+)(.*Mobile)?.*Safari.*").exec(m))) && 16.4 <= parseFloat(m[1]),
      f = "stapeUserId" === g,
      I = d && !f ? function(e, t, r) {
        void 0 === t && (t = "");
        var n = { cookie: l, localStorage: s, jsVariable: u, cssSelector: A },
          t = Array.isArray(t) ? t : [t];
        if (e && n[e])
          for (var o = n[e], a = 0, i = t; a < i.length; a++) {
            var c = i[a], c = r ? o(c, r) : o(c);
            if (c) return c
          } else console.warn("invalid uid source", e)
      }(g, v, E) : void 0;
    d = d && (!!I || f)
  } catch (e) { console.error(e) }
  var m = e,
    g = (m[n] = m[n] || [], m[n].push({ "gtm.start": (new Date).getTime(), event: "gtm.js" }), t.getElementsByTagName(r)[0]),
    v = I ? "&bi=" + encodeURIComponent(I) : "",
    E = t.createElement(r),
    f = (d && (i = 8 < i.length ? i.replace(/([a-z]{8}$)/, "kp$1") : "kp" + i), !d && a ? a : o);
  E.async = !0, E.src = f + "/" + i + ".js?" + c + v, null != (e = g.parentNode) && e.insertBefore(E, g)
}();`;

  const braidrUatNoScript =
    '<iframe src="https://load.ss-uat.digitalrealty.com/ns.html?id=GTM-5ZXBHKRJ" height="0" width="0" style="display:none;visibility:hidden"></iframe>';

  // Braidr server-side GTM loader for production (container GTM-T2MV27 via
  // ss.digitalrealty.com). Derived from the UAT loader above by swapping only
  // the two stape hosts and the signed config blob, so the two cannot drift
  // apart in anything else.
  const braidrProdLoader = `!function() {
  "use strict";
  function l(e) {
    for (var t = e, r = 0, n = document.cookie.split(";"); r < n.length; r++) {
      var o = n[r].split("=");
      if (o[0].trim() === t) return o[1]
    }
  }
  function s(e) { return localStorage.getItem(e) }
  function u(e) { return window[e] }
  function A(e, t) {
    e = document.querySelector(e);
    return t ? null == e ? void 0 : e.getAttribute(t) : null == e ? void 0 : e.textContent
  }
  var e = window, t = document, r = "script", n = "dataLayer",
    o = "https://ss.digitalrealty.com",
    a = "https://load.ss.digitalrealty.com",
    i = "d1p8zzzpqvvh",
    c = "d0cp4k=ARJLNiQ3Vy5aOyBDR1wMR1pYR1cUFEceAREfBREWCB8JGgIIXhkVFw%3D%3D",
    g = "stapeUserId", v = "", E = "", d = !1;
  try {
    var d = !!g && (m = navigator.userAgent, !!(m = new RegExp("Version/([0-9._]+)(.*Mobile)?.*Safari.*").exec(m))) && 16.4 <= parseFloat(m[1]),
      f = "stapeUserId" === g,
      I = d && !f ? function(e, t, r) {
        void 0 === t && (t = "");
        var n = { cookie: l, localStorage: s, jsVariable: u, cssSelector: A },
          t = Array.isArray(t) ? t : [t];
        if (e && n[e])
          for (var o = n[e], a = 0, i = t; a < i.length; a++) {
            var c = i[a], c = r ? o(c, r) : o(c);
            if (c) return c
          } else console.warn("invalid uid source", e)
      }(g, v, E) : void 0;
    d = d && (!!I || f)
  } catch (e) { console.error(e) }
  var m = e,
    g = (m[n] = m[n] || [], m[n].push({ "gtm.start": (new Date).getTime(), event: "gtm.js" }), t.getElementsByTagName(r)[0]),
    v = I ? "&bi=" + encodeURIComponent(I) : "",
    E = t.createElement(r),
    f = (d && (i = 8 < i.length ? i.replace(/([a-z]{8}$)/, "kp$1") : "kp" + i), !d && a ? a : o);
  E.async = !0, E.src = f + "/" + i + ".js?" + c + v, null != (e = g.parentNode) && e.insertBefore(E, g)
}();`;

  const braidrProdNoScript =
    '<iframe src="https://load.ss.digitalrealty.com/ns.html?id=GTM-T2MV27" height="0" width="0" style="display:none;visibility:hidden"></iframe>';

  const generateGtmScripts = (gtmAuth, gtmPreview, isStagingEnv, isUatEnv) => {
    const headScript = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl+'&gtm_auth=${gtmAuth}&gtm_preview=${gtmPreview}&gtm_cookies_win=x';f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','GTM-T2MV27');`;

    const noScript = `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-T2MV27&gtm_auth=${gtmAuth}&gtm_preview=${gtmPreview}&gtm_cookies_win=x"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

    const stagingGTMScript = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-5ZXBHKRJ');`;

    const stagingGTMNoScript = `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-5ZXBHKRJ"
height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

    // UAT runs exclusively on the Braidr loader. Checked before the generic
    // staging branch, which also covers dlr/localhost.
    if (isUatEnv) {
      return { headScript: braidrUatLoader, noScript: braidrUatNoScript };
    }

    if (isStagingEnv) {
      return { headScript: stagingGTMScript, noScript: stagingGTMNoScript };
    }

    // Production runs exclusively on the Braidr loader. The standard
    // googletagmanager.com pair above is now reachable by nothing — it is left
    // in place only as the shape this replaced.
    return { headScript: braidrProdLoader, noScript: braidrProdNoScript };
  };

  useEffect(() => {
    const host = window.location.host;

    let config;
    let isStagingEnv = false;
    let isUatEnv = false;
    if (PROD_HOSTNAMES.includes(host)) {
      config = GTM_CONFIG.prod;
      isStagingEnv = false;
    } else if (host.includes('uat')) {
      config = GTM_CONFIG.uat;
      isStagingEnv = true;
      isUatEnv = true;
    } else if (host.includes('dlr') || host.includes('localhost')) {
      config = GTM_CONFIG.dlr;
      isStagingEnv = true;
    }
    window.isStagingEnv = isStagingEnv;
    // Read by the Germany consent script below, which injects GTM itself after
    // consent and therefore needs to know which loader to use.
    window.isUatEnv = isUatEnv;

    if (config) {
      const { gtmAuth, gtmPreview } = config;
      const { headScript, noScript } = generateGtmScripts(
        gtmAuth,
        gtmPreview,
        isStagingEnv,
        isUatEnv
      );
      const oneTrustScript = getScriptForLanguage(lang, false, isStagingEnv);

      setHeadScript(headScript);
      setNoScript(noScript);
      setOneTrustScript(oneTrustScript);
    }
  }, [setHeadScript, setNoScript]);

  //TODO: add uat script below

  const germanyConsentScript = `
/**

IMPLEMENTATION

- If user is from Germany:

1) Set Consent Mode defaults to "denied"

2) Load OneTrust SDK

3) When Targeting (C0002) is granted, flip consent to granted and inject GTM

- Else (non-Germany): do nothing (default behavior handled elsewhere)

 *

NOTES:

- Uses window.__isDE or window.userCountry ('DE'/'Germany') for detection

- Selects OneTrust Domain Script ID by domain (.de can have its own)

- No <noscript>

 */

// ---- REQUIRED CONFIG ----

var GTM_ID = window.isStagingEnv ? 'GTM-5ZXBHKRJ' : 'GTM-T2MV27'

// OneTrust Domain Script IDs

var DEFAULT_OT_ID = window.isStagingEnv ? '019b8ef6-880d-7d4f-aa9c-a69d207d2f53' : 'ec376bc6-9b0b-4f57-a6d8-2d6c73d285c5';

// Required category to allow GTM

var REQUIRED_CATEGORIES = ['C0002']; // Targeting

// ---- GEO DETECTION ----

var __isFromGermany = !!window.__isDE || (window.userCountry === 'DE' || window.userCountry === 'Germany');

// ---- HELPERS ----

function isGermanyDomain(){ return (location.hostname || '').toLowerCase().endsWith('.de'); }

function getOtIdForHost(){ return DEFAULT_OT_ID; }

function hasRequiredConsent(){

  var groups = String(window.OnetrustActiveGroups || '');

  return REQUIRED_CATEGORIES.every(cat => groups.includes(cat));

}

function loadGTM(){

  if (window.__gtmLoaded) return;

  window.__gtmLoaded = true;

  // UAT loads the Braidr server-side loader instead of the standard
  // googletagmanager.com snippet. Only the LOADER changes here — this function
  // is still called at exactly the same point in the consent flow, so German
  // users continue to get GTM only after Targeting consent is granted.
  //
  // The host is read directly rather than via window.isUatEnv, which is set in
  // a useEffect: a returning visitor with stored consent can have OneTrust fire
  // OptanonWrapper before hydration, and the flag would still be undefined —
  // which would silently fall through to the standard loader on UAT.
  if ((location.hostname || '').toLowerCase().indexOf('uat') !== -1) {

    ${braidrUatLoader}

    return;

  }

  // Production swaps the loader the same way. This function is still called at
  // exactly the same point in the consent flow, so German visitors continue to
  // get GTM only after Targeting consent is granted.
  //
  // Matched against PROD_HOSTNAMES, the list the rest of this file already uses
  // — not window.isStagingEnv, which is set in a useEffect and can still be
  // undefined when a returning visitor's stored consent fires OptanonWrapper
  // before hydration.
  if (${JSON.stringify(PROD_HOSTNAMES)}.indexOf(location.host) !== -1) {

    ${braidrProdLoader}

    return;

  }

  (function(w,d,s,l,i){

    w[l]=w[l]||[];

    w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});

    var f=d.getElementsByTagName(s)[0], j=d.createElement(s);

    j.async = true; j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i;

    f.parentNode.insertBefore(j,f);

  })(window,document,'script','dataLayer',GTM_ID);

}

function loadOneTrust(otId){

  if (window.__otLoaded) return;

  window.__otLoaded = true;

  var s = document.createElement('script');

  s.async = true;

  s.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';

  s.setAttribute('data-domain-script', otId);

  document.head.appendChild(s);

}

// ---- MAIN ----

(function(){

  if (__isFromGermany) {

    window.dataLayer = window.dataLayer || [];

    function gtag(){ dataLayer.push(arguments); }

    gtag('consent','default',{

      ad_storage: 'denied',

      analytics_storage: 'denied',

      ad_user_data: 'denied',

      ad_personalization: 'denied',

      functionality_storage: 'denied',

      personalization_storage: 'denied',

      security_storage: 'granted',

      wait_for_update: 2000

    });

    var otId = getOtIdForHost();

    window.OptanonWrapper = function(){

      if (hasRequiredConsent()) {

        gtag('consent','update',{

          ad_storage:'granted',

          analytics_storage:'granted',

          ad_user_data:'granted',

          ad_personalization:'granted',

          functionality_storage:'granted',

          personalization_storage:'granted',

          security_storage:'granted'

        });

        loadGTM();

      } else if (window.OneTrust && OneTrust.OnConsentChanged) {

        OneTrust.OnConsentChanged(function(){

          if (hasRequiredConsent()){

            gtag('consent','update',{

              ad_storage:'granted',

              analytics_storage:'granted',

              ad_user_data:'granted',

              ad_personalization:'granted',

              functionality_storage:'granted',

              personalization_storage:'granted',

              security_storage:'granted'

            });

            loadGTM();

          }

        });

      }

    };

    loadOneTrust(otId);

  }

})();
`;

  return (
    <>
      {data?.content_type === 'author' && <AuthorSchema data={[data]} />}
      {data?.field_authors && <AuthorSchema data={data?.field_authors} />}
      {!isBrowser && (
        <Head>
          {/*{getScriptForLanguage(lang)}*/}

          {/*<script*/}
          {/*    dangerouslySetInnerHTML={{*/}
          {/*      __html: `${headScript}`,*/}
          {/*    }}*/}
          {/*/>*/}
          <script dangerouslySetInnerHTML={{ __html: dataLayerScript }} />

          {oneTrustScript}

          {!userCountryData.isLoading && (
            <script
              dangerouslySetInnerHTML={{
                __html: `${germanyConsentScript}`,
              }}
            />
          )}

          {!userCountryData.isLoading && !userCountryData.isDE && (
            <script
              dangerouslySetInnerHTML={{
                __html: `console.log("headScript - executing for non-DE user"); ${headScript}`,
              }}
            />
          )}

          <link rel="shortcut icon" href="/favicon/favicon1.png" />
          <title>
            {titleTag ? titleTag : 'Digital Realty | Data Center Services'}
          </title>
          {renderAlternateLinks(existingPages, Constants)}
          <link href={Constants[getDomainAlias(lang)]} rel="home" />
          {getCanonical(canonicalUrl, lang, urlAlias, paginationPage)}
          {getPaginationRelPrev(lang, urlAlias, paginationPage)}
          {getSearchRobotsMeta(router?.query)}
          <meta name="urlAlias" content={urlAlias} />
          <meta name="language" content={getLanguageContent(lang)} />
          {lang === 'ko' ? (
            <meta
              name="naver-site-verification"
              content="naver298f5ae2747288292ba000078130e694"
            />
          ) : (
            ''
          )}
        </Head>
      )}
      {isBrowser && (
        <Head>
          {/*{getScriptForLanguage(lang)}*/}
          {/*<script*/}
          {/*    dangerouslySetInnerHTML={{*/}
          {/*      __html: `${headScript}`,*/}
          {/*    }}*/}
          {/*/>*/}

          <script dangerouslySetInnerHTML={{ __html: dataLayerScript }} />

          {oneTrustScript}

          {!userCountryData.isLoading && (
            <script
              dangerouslySetInnerHTML={{
                __html: `${germanyConsentScript}`,
              }}
            />
          )}

          {!userCountryData.isLoading && !userCountryData.isDE && (
            <script
              dangerouslySetInnerHTML={{
                __html: `console.log("headScript - executing for non-DE user"); ${headScript}`,
              }}
            />
          )}

          <title>
            {titleTag ? titleTag : 'Digital Realty | Data Center Services'}
          </title>
          {renderMetaTags(metaTags)}
          {renderAlternateLinks(existingPages, Constants)}
          <link href={Constants[getDomainAlias(lang)]} rel="home" />
          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href={origin ? origin + '/favicon/apple-touch-icon.png' : ''}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="32x32"
            href={origin ? origin + '/favicon/favicon-32x32.png' : ''}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href={origin ? origin + '/favicon/favicon-16x16.png' : ''}
          />
          <link rel="shortcut icon" href="/favicon/favicon1.png" />
          <link
            rel="manifest"
            href={origin ? origin + '/favicon/site.webmanifest' : ''}
          />
          {heroBlock ? (
            <meta
              key={`meta-hero-banner}`}
              name={'og:image'}
              content={`https://res.cloudinary.com/digitalrealty/image/upload/${heroBlock?.hero_block?.field_hero_image?.webimage}`}
            ></meta>
          ) : (
            <></>
          )}
          <meta name="msapplication-TileColor" content="#da532c" />
          <meta name="theme-color" content="#ffffff" />
          <meta
            name="keywords"
            content={meta ? meta[0]?.field_meta_tags_keywords : ''}
          />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1"
          />
          <script
            src="https://kit.fontawesome.com/f5ffb738a2.js"
            crossOrigin="anonymous"
            defer
          />
          <link
            rel="stylesheet"
            href="https://kit.fontawesome.com/f5ffb738a2.css"
            crossOrigin="anonymous"
          />
          {getCanonical(canonicalUrl, lang, urlAlias, paginationPage)}
          {getPaginationRelPrev(lang, urlAlias, paginationPage)}
          {getSearchRobotsMeta(router?.query)}
          <meta name="urlAlias" content={urlAlias} />
          <meta name="language" content={getLanguageContent(lang)} />
          {lang === 'ko' ? (
            <meta
              name="naver-site-verification"
              content="naver298f5ae2747288292ba000078130e694"
            />
          ) : (
            ''
          )}
        </Head>
      )}

      {isIxnquery && lang !== 'en' ? (
        getLandPageFromRedirect(lang, device, ixnRedirectMessages)
      ) : (
        <>
          <noscript
            dangerouslySetInnerHTML={{
              __html: `${noScript}`,
            }}
          ></noscript>
          <div className={showBg ? 'gray-bg active' : 'gray-bg'} />
          <div
            className={
              showSearchBg ? 'show-search-bg active' : 'show-search-bg'
            }
          />
          <Header
            urlAlias={urlAlias}
            endpoint={endpoint}
            screenWidth={viewportWidth}
            countryData={countryData}
            pageTranslationPaths={children?.props?.data?.translang}
            hideHeader={isItUndefined(data?.field_hide_footer)}
            toggleShowSearchBg={toggleShowSearchBg}
            onBg={onBg}
            offBg={offBg}
            globalMetrics={globalMetrics}
            lang={lang}
            data={menuData}
            languageSelectorData={languageSelectorData}
          />
          {!hideElements?.hideBreadCrumbs && (
            <Breadcrumb
              contentType={contentType}
              hideHeader={isItUndefined(data?.field_hide_footer)}
              urlAlias={urlAlias}
              data={data}
              lang={lang}
              globalMetrics={globalMetrics}
            />
          )}
          <div className="overlay-scrollbar-track">
            <div className="overlay-scrollbar-thumb"></div>
          </div>
          {sendHome ? '' : <main>{children}</main>}
          {!hideElements?.hideFooter && (
            <Footer
              hideFoooter={isItUndefined(data?.field_hide_footer)}
              data={footerData}
              globalMetrics={globalMetrics}
              lang={lang}
              endpoint={endpoint}
            />
          )}
        </>
      )}
    </>
  );
};

export default Layout;
